import { computed, type WritableComputedRef } from 'vue'

// --- 数値フォーマット ---

export function formatNumber(
  value?: number,
  options?: { compact?: boolean; fallbackLabel?: string },
): string {
  const { compact = false, fallbackLabel = '-' } = options ?? {}
  if (value === undefined) return fallbackLabel
  if (!compact) return value.toLocaleString('ja-JP')

  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${Math.round((value / 1_000_000_000) * 10) / 10}B`
  if (abs >= 1_000_000) return `${Math.round((value / 1_000_000) * 10) / 10}M`
  if (abs >= 1_000) return `${Math.round((value / 1_000) * 10) / 10}K`
  return `${value}`
}

export function formatPercent(
  value?: number,
  options?: { digits?: number; fallbackLabel?: string },
): string {
  const { digits = 1, fallbackLabel = '-' } = options ?? {}
  if (value === undefined) return fallbackLabel
  return `${(value * 100).toFixed(digits)}%`
}

export function formatDecimal(
  value?: number,
  options?: { digits?: number; fallbackLabel?: string },
): string {
  const { digits = 2, fallbackLabel = '-' } = options ?? {}
  if (value === undefined) return fallbackLabel
  return value.toFixed(digits)
}

// 差分・増減表示用。正の値・0 は "+" prefix を付ける（負の値は toLocaleString が "-" を付ける）。
export function formatDifference(value?: number, fallbackLabel = '-'): string {
  if (value === undefined) return fallbackLabel
  if (value >= 0) return `+${value.toLocaleString('ja-JP')}`
  return value.toLocaleString('ja-JP')
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function formatPrice(amount?: number | string): string {
  if (amount === undefined || amount === '') return '-'
  const n = Number(amount)
  if (!Number.isFinite(n)) return '-'
  return usd.format(n)
}

export function toPositiveInt(value: unknown): number | undefined {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

// --- 入力連携 ---

// ref フィールドをカンマ区切り文字列で表示・入力する writable computed。全角数字も受ける。
// 空入力は undefined にする。required な number フィールド (`Ref<number>`) に繋ぐ場合は
// setter 側で default を当てる: `(n) => salaryYen.value = n ?? 0`
export function numericDisplay(
  getter: () => number | undefined,
  setter: (v: number | undefined) => void,
): WritableComputedRef<string> {
  return computed({
    get: () => {
      const v = getter()
      return v === undefined ? '' : v.toLocaleString('ja-JP')
    },
    set: (v: string) => {
      const stripped = stripToDigits(v)
      setter(stripped ? Number(stripped) : undefined)
    },
  })
}

function toHalfWidth(value: string): string {
  return value.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
}

function stripToDigits(value: string): string {
  return toHalfWidth(value).replace(/[^0-9]/g, '')
}

// パーセント入力文字列を 0-1 decimal に変換する。
// "50" → 0.5, "3.5" → 0.035, 空文字・不正値は undefined。
export function parseRateInput(text: string): number | undefined {
  const normalized = toHalfWidth(text).replace(/．/g, '.').trim()
  if (!normalized) return undefined
  const n = Number(normalized)
  if (Number.isNaN(n)) return undefined
  return n / 100
}

// Element Plus el-input-number の :formatter / :parser に渡す。
// formatter は EP が parse 済みの model 値 (number | undefined) を受ける。全角→半角は parser の責務。
// `<el-input-number :formatter="formatter" :parser="elInputNumberParser" />`
export function elInputNumberFormatter(options?: {
  comma?: boolean
}): (val: number | undefined) => string {
  const { comma = false } = options ?? {}
  return (val) => {
    if (val === undefined) return ''
    return comma ? val.toLocaleString() : String(val)
  }
}

export function elInputNumberParser(val: string): string {
  return toHalfWidth(val).replace(/[,，]/g, '')
}

// --- 日付フォーマット ---

export type DateValue = string | Date | undefined

const pad2 = (value: number) => String(value).padStart(2, '0')

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const toDate = (value: DateValue): Date | undefined => {
  if (!value) return undefined
  // new Date('YYYY-MM-DD') は UTC の 0 時になり、UTC より西のタイムゾーンでは前日に表示される。
  // 時刻を持たない日付はローカルの 0 時として扱う
  const dateOnly = typeof value === 'string' ? DATE_ONLY_PATTERN.exec(value) : null
  if (dateOnly) {
    const [, year, month, day] = dateOnly.map(Number)
    return new Date(year, month - 1, day)
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return undefined
  return d
}

export function formatDate(
  value?: DateValue,
  options?: { withTime?: boolean; noYear?: boolean; todayAsTime?: boolean },
): string {
  const d = toDate(value)
  if (!d) return '-'
  if (options?.todayAsTime) {
    const now = new Date()
    if (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    ) {
      return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    }
  }
  const yyyy = d.getFullYear()
  const mm = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  const date = options?.noYear ? `${mm}/${dd}` : `${yyyy}/${mm}/${dd}`
  if (options?.withTime) {
    return `${date} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
  }
  return date
}

// 24h 以内は相対表示（just now / N min ago / N h ago）、それ以降は日付（同年は年を省く）。
export function formatRelativeTime(value?: DateValue): string {
  const d = toDate(value)
  if (!d) return '-'
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const dayMs = 24 * 60 * 60 * 1000
  if (diffMs >= dayMs) return formatDate(d, { noYear: d.getFullYear() === now.getFullYear() })
  const minutes = Math.floor(diffMs / (60 * 1000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  return `${Math.floor(minutes / 60)} h ago`
}

export function formatDateRange(start?: DateValue, end?: DateValue): string {
  if (!start && !end) return 'Not set'
  if (start && !end) return `${formatDate(start)} -`
  if (!start && end) return `- ${formatDate(end)}`
  return `${formatDate(start)} - ${formatDate(end)}`
}

// Date → YYYY-MM-DD 文字列。input[type=date] への bind 用。
export function toInputDate(value: Date): string
export function toInputDate(value?: DateValue): string | undefined
export function toInputDate(value?: DateValue): string | undefined {
  const d = toDate(value)
  if (!d) return undefined
  const yyyy = d.getFullYear()
  const mm = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  return `${yyyy}-${mm}-${dd}`
}
